import { useCallback, useContext, useState } from "react";
import type { CSSProperties } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { formatDistanceToNow } from "date-fns";
import styled, { ThemeContext } from "styled-components";

import {
  LabelChooser,
  LabelItem,
} from "simplydo/react/components/Choosers/LabelChooser/LabelChooser";
import {
  Button,
  Container,
  Dropdown,
  Grid,
  Icon,
  Popup,
  Transition,
} from "simplydo/react/ui";

import actions from "actions";
import api from "api";
import { useAppDispatch, useAppSelector } from "store";
import util from "utils/utils";

import {
  AnonymousChip,
  IdeaBusinessProfileChip,
  UserChip,
} from "components/lib/Chips";
import IdeaPdfExport from "components/lib/IdeaPdfExport";
import { IdeaLikeHandler } from "components/lib/Ideas";
import type {
  IdeaPreviewIdea,
  IdeaPreviewOwnerChallenge,
  IdeaPreviewProjectBoard,
} from "components/lib/Ideas/IdeaPreview/types";
import PinIdeaHelper from "components/lib/Ideas/PinIdeaHelper";
import { Banner, PlaceholderItem, PlaceholderStack } from "components/lib/UI";

import IdeaTags from "./Tags";
import { useIdea } from "./hooks/useIdea";

const OptionsMenu = styled(Dropdown.Menu)`
  width: 300px;
  min-width: 0 !important;
  .labels {
    display: flex;
    flex-wrap: wrap;
    .label {
      width: 50%;
    }
    .label:hover {
      background-color: #e9ebee95;
      cursor: pointer;
    }
  }
`;

const StyledLaneInfo = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  line-height: 1;
  color: white;
  > i.icon {
  }
  > div {
    display: flex;
    flex-direction: column;
    > span {
      display: block;
      margin: 0;
    }
    .title-info {
      font-size: 0.8em;
    }
    .lane-name {
      font-weight: bold;
      font-size: 1em;
      color: white;
    }
  }
`;

export const LaneInfo = ({
  board,
  idea,
  style,
}: {
  board?: IdeaPreviewProjectBoard;
  idea?: IdeaPreviewIdea;
  style?: CSSProperties;
}) => {
  if (idea?._id && board?.laneObject?.name) {
    if (board.laneObject?.laneCount === 0) return null;
    return (
      <Popup
        disabled={!board.movedAt}
        trigger={
          <StyledLaneInfo style={style}>
            <Icon name="compass" />
            <div>
              <span className="title-info">PROJECT LANE</span>
              <Link
                className="lane-name"
                to={`/challenges/${idea?.challenge}/ideas?queryView=board&idea=${idea?._id}`}
              >
                {board.laneObject.name}
              </Link>
            </div>
          </StyledLaneInfo>
        }
        content={`${board.movedAt ? `Moved ${formatDistanceToNow(new Date(board.movedAt), { addSuffix: true })}` : ""} ${board.movedBy ? ` by ${board.movedBy?.profile?.fullName}` : ""}`}
      />
    );
  }
  return null;
};

type IdeaHeaderProps = {
  isEditing: boolean;
  updateIdea: (data: unknown, path: string[], save?: boolean) => void;
  deleteIdea: () => void;
  leaveIdea: () => void;
};

const IdeaHeader = ({
  isEditing,
  updateIdea,
  deleteIdea,
  leaveIdea,
}: IdeaHeaderProps) => {
  const [idea, ideaQuery] = useIdea();
  const loading = ideaQuery.isLoading;
  const user = useAppSelector((state) => state.user);
  const dispatch = useAppDispatch();

  const { t } = useTranslation();

  const onFollow = useCallback(
    (context: string, id: string) => {
      dispatch(actions.user.onFollow(context, id));
    },
    [dispatch],
  );

  const updateFilters = useCallback(
    (data: any) => {
      dispatch(actions.challenges.updateIdeaFilters(data));
    },
    [dispatch],
  );

  const theme = useContext(ThemeContext);
  const [labelChooserOpen, setLabelChooserOpen] = useState(false);
  const ideaId = idea?._id;
  const userId = user?._id;
  const ideaChallenge = idea?.challenge;
  const canManageChallenge = util.canManageChallenge(
    user!,
    idea?.ownerChallenge as IdeaPreviewOwnerChallenge | undefined,
  );

  const navigate = useNavigate();

  const joinIdea = useCallback(() => {
    if (!ideaId || !userId) return;
    api.ideas.addCollaborator(
      ideaId,
      userId,
      ({ authors, collaborators }) => {
        updateIdea(authors, ["authors"], true);
        updateIdea(collaborators, ["collaborators"], true);
      },
      (err) => toast.error(err.message),
    );
  }, [ideaId, userId, updateIdea]);

  const findIdeasWithTag = useCallback(
    (value: string) => {
      updateFilters({ ideaFilter: value });
    },
    [updateFilters],
  );

  const voteForIdea = useCallback(
    (addVote: boolean) => {
      if (!ideaId) return;
      api.ideas.vote(
        ideaId,
        addVote,
        ({ addedVotesFor, removedVotesFor }) => {
          const newIdea = {
            votedFor: idea?.votedFor,
            voteCount: idea?.voteCount || 0,
          };

          if (!newIdea.voteCount) newIdea.voteCount = 0;
          if (addedVotesFor.indexOf(ideaId) > -1) {
            onFollow("challenge", ideaChallenge);
            newIdea.votedFor = true;
            newIdea.voteCount += 1;
          }
          if (removedVotesFor.indexOf(ideaId) > -1) {
            newIdea.votedFor = false;
            newIdea.voteCount -= 1;
          }
          updateIdea(newIdea.voteCount, ["voteCount"], true);
          updateIdea(newIdea.votedFor, ["votedFor"], true);
        },
        (err) => {
          toast.error(err.message);
        },
      );
    },
    [
      ideaId,
      updateIdea,
      ideaChallenge,
      idea?.votedFor,
      idea?.voteCount,
      onFollow,
    ],
  );

  const pinIdea = useCallback(() => {
    if (!ideaId) return;
    api.ideas.updateStatus(
      ideaId,
      { isPinned: !idea?.isPinned },
      (ideaStatus) => {
        updateIdea(ideaStatus.isPinned, ["isPinned"], true);
      },
      (err) => toast.error(err.message),
    );
  }, [ideaId, idea?.isPinned, updateIdea]);

  const labelIdea = useCallback(
    (label: string, isApplied: boolean) => {
      if (!ideaId) return;
      let labels = idea?.labels || [];
      if (labels.includes(label)) {
        if (!isApplied) {
          labels = labels.filter((l) => l !== label);
        }
      } else {
        if (isApplied) {
          labels = [...labels, label];
        }
      }

      api.ideas.updateStatus(
        ideaId,
        { labels },
        () => {
          updateIdea(labels, ["labels"], true);
        },
        (err) => toast.error(err.message),
      );
    },
    [ideaId, idea?.labels, updateIdea],
  );

  const createChallengeFromIdea = useCallback(() => {
    if (!ideaId) return;
    api.challenges.createFromIdea(
      { idea: ideaId },
      (data) => {
        navigate(`/challenges/${data.challenge._id}`);
        toast.success("Challenge created");
      },
      (err) => toast.error(err.message),
    );
  }, [ideaId, navigate]);

  if (isEditing) return null;
  const board = idea?.projectManagement?.boards?.filter(
    (b) => b.forId === idea?.challenge,
  )[0] as IdeaPreviewProjectBoard | undefined;
  return (
    <Transition visible={!isEditing} animation="slide down" duration={400}>
      <Banner
        marginless
        image={util.mixinCssUrlFallback(
          util.ideaCoverImage(idea ?? undefined),
          util.ideaCoverImage(undefined),
        )}
        style={{ zIndex: 200 }}
      >
        <LabelChooser
          isOpen={labelChooserOpen}
          onClose={() => setLabelChooserOpen(false)}
          onComplete={(em) => labelIdea(em, true)}
          existing={idea?.labels}
          onRemoveExisting={(em) => labelIdea(em, false)}
        />
        <div className="overlay" />
        <div className="overlay-body">
          <Container>
            <Grid stackable>
              <Grid.Column computer={16}>
                {loading ?
                  <PlaceholderStack
                    gap={10}
                    style={{ marginBottom: 30, marginTop: 30 }}
                  >
                    <PlaceholderItem height={30} />
                    <PlaceholderItem height={10} />
                  </PlaceholderStack>
                : <>
                    <h1 style={{ margin: "0 0 10px 0" }}>
                      {idea?.name ||
                        `${t("common:capitalise", { key: "generic.ideas" })} ${idea?.ownerChallenge?.name ? `for ${idea?.ownerChallenge?.name}` : ""}`}
                    </h1>

                    <h5
                      style={{
                        fontSize: 14,
                        marginTop: 0,
                        marginBottom: board?.laneObject?.name ? 10 : 0,
                      }}
                    >
                      <span style={{ textTransform: "uppercase" }}>
                        {t("generic.idea")}
                      </span>
                      {idea?.ownerChallenge && (
                        <span style={{ color: "white" }}>
                          {" "}
                          For{" "}
                          <Link
                            style={{ color: "white" }}
                            to={`/challenges/${idea?.challenge}`}
                          >
                            {idea?.ownerChallenge?.name}
                          </Link>
                        </span>
                      )}
                    </h5>
                    {canManageChallenge && (
                      <LaneInfo
                        board={board}
                        idea={idea}
                        style={{ marginBottom: 20 }}
                      />
                    )}

                    {idea ?
                      <IdeaTags idea={idea} updateIdea={updateIdea} />
                    : null}
                    <div>
                      {idea?.labels?.map((label) => (
                        <LabelItem key={label} label={label} />
                      ))}
                      {(
                        util.hasPermission(
                          user!,
                          "challenge.editIdeaLabels",
                          idea?.challenge,
                        )
                      ) ?
                        <Button
                          size="mini"
                          compact
                          onClick={() => setLabelChooserOpen(true)}
                          style={{
                            marginLeft: 8,
                            fontSize: 10,
                            padding: "0.2em 0.6em",
                          }}
                        >
                          <Icon name="bookmark" /> Edit Labels
                        </Button>
                      : null}
                    </div>
                  </>
                }
              </Grid.Column>
              <Grid.Column
                computer={12}
                textAlign="left"
                verticalAlign="bottom"
              >
                {loading ?
                  <PlaceholderItem height={15} style={{ width: "50%" }} />
                : <div style={{ color: "#fff" }}>
                    {"Created by "}
                    {!idea?.authors || idea?.authors.length === 0 ?
                      <AnonymousChip />
                    : null}
                    {idea?.lockedOwnerIdeaBusinessProfile ?
                      <IdeaBusinessProfileChip
                        inverted
                        ideaBusinessProfile={
                          idea?.lockedOwnerIdeaBusinessProfile
                        }
                      />
                    : null}
                    {(idea?.authors || []).map((a) => (
                      <UserChip
                        key={a._id}
                        inverted
                        style={{ margin: 5 }}
                        user={a}
                      />
                    ))}
                  </div>
                }
              </Grid.Column>
              <Grid.Column
                computer={4}
                textAlign="right"
                verticalAlign="bottom"
              >
                <>
                  {(
                    idea?.ownerChallenge &&
                    idea?.ownerChallenge.votingVisibility === "users"
                  ) ?
                    <IdeaLikeHandler
                      idea={idea}
                      challenge={idea?.ownerChallenge}
                      onLikeIdea={(_ideaId, addVote) => voteForIdea(addVote)}
                      trigger={
                        <Button
                          basic={!idea?.votedFor}
                          primary={idea?.votedFor}
                          icon="thumbs up"
                          style={{ marginLeft: 5 }}
                          size="tiny"
                          label={
                            (
                              idea?.ownerChallenge.voteCountVisibility ===
                              "users"
                            ) ?
                              {
                                basic: true,
                                pointing: "left",
                                content: idea?.voteCount || 0,
                              }
                            : null
                          }
                        />
                      }
                    />
                  : null}

                  {(
                    util.canManageChallenge(
                      user!,
                      idea?.ownerChallenge as
                        | IdeaPreviewOwnerChallenge
                        | undefined,
                    ) ||
                    util.hasPermission(
                      user!,
                      "challenge.pinIdeas",
                      idea?.challenge,
                    )
                  ) ?
                    <Popup
                      content={
                        <div>
                          <p>
                            <b>
                              {idea?.isPinned ?
                                t("ideas.options.pin.unPin")
                              : t("ideas.options.pin.challenge")}
                            </b>
                          </p>

                          <PinIdeaHelper challenge={idea.ownerChallenge!} />
                        </div>
                      }
                      trigger={
                        <Button
                          size="small"
                          primary={idea?.isPinned}
                          basic={!idea?.isPinned}
                          icon="thumbtack"
                          onClick={pinIdea}
                        />
                      }
                    />
                  : null}

                  {idea ?
                    <IdeaPdfExport
                      trigger={<Button size="small" icon="file pdf" basic />}
                      ideas={[idea]}
                      challenge={idea?.ownerChallenge}
                    />
                  : null}

                  {(
                    util.canManageChallenge(
                      user!,
                      idea?.ownerChallenge as
                        | IdeaPreviewOwnerChallenge
                        | undefined,
                    ) ||
                    util.hasPermission(
                      user!,
                      "challenge.moveIdeaToChallenge",
                      idea?.challenge,
                    ) ||
                    util.hasPermission(
                      user!,
                      "challenge.editIdeaLabels",
                      idea?.challenge,
                    ) ||
                    util.hasPermission(
                      user!,
                      "org.managePermissions",
                      user!.ownerOrganisation?._id,
                    )
                  ) ?
                    <Dropdown
                      icon={null}
                      direction="left"
                      trigger={
                        <Button
                          basic
                          size="small"
                          icon="chevron down"
                          data-testid="idea-options-dropdown"
                        />
                      }
                    >
                      <OptionsMenu>
                        {user && idea?.user !== user._id && (
                          <Dropdown.Item
                            icon={`user ${util.canEditIdea(user, idea) ? "times" : "plus"}`}
                            content={
                              util.canEditIdea(user, idea) ?
                                `Leave ${t("generic.idea")}`
                              : `Collaborate on this ${t("generic.idea")}`
                            }
                            onClick={
                              util.canEditIdea(user, idea) ? leaveIdea : (
                                joinIdea
                              )
                            }
                          />
                        )}
                        {(
                          util.hasPermission(
                            user!,
                            "challenge.moveIdeaToChallenge",
                            idea?.challenge,
                          ) && idea?.challengeCreation
                        ) ?
                          <Dropdown.Item
                            icon="target"
                            onClick={() => createChallengeFromIdea()}
                          >
                            Create {t("generic.challengeWithArticle")} from this{" "}
                            {t("generic.idea")}
                          </Dropdown.Item>
                        : null}

                        <Dropdown.Divider />

                        {(
                          !idea?.ownerChallenge?.preventIdeaCoverImages &&
                          idea?.coverImage
                        ) ?
                          <Dropdown.Item
                            as="a"
                            href={idea?.coverImageUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            content="Download cover image"
                            icon="download"
                          />
                        : null}

                        {(
                          util.hasPermission(
                            user!,
                            "challenge.editIdeaLabels",
                            idea?.challenge,
                          )
                        ) ?
                          <Dropdown.Item
                            icon="bookmark"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setLabelChooserOpen(true);
                            }}
                            content="Apply a label"
                          />
                        : null}

                        {(
                          util.canEditIdea(user!, idea) ||
                          util.hasPermission(
                            user!,
                            "org.managePermissions",
                            user!.ownerOrganisation?._id,
                          )
                        ) ?
                          <>
                            <Dropdown.Divider />
                            <Dropdown.Item
                              icon="trash"
                              content={`Delete ${t("generic.idea")}`}
                              onClick={() => deleteIdea()}
                            />
                          </>
                        : null}
                      </OptionsMenu>
                    </Dropdown>
                  : null}
                </>
              </Grid.Column>
            </Grid>
          </Container>
        </div>
      </Banner>
    </Transition>
  );
};

export default IdeaHeader;
